// @purpose VIEW
// /pressure/ island (templates/consumer/pressure/pressure.html). Progressive enhancement only: the page,
// its quiz CTA and the stage's final frame (the poster) are complete without this file.
//  1. Stage: after load + idle, when the stage is near the viewport and WebGL2 is available, the raymarch
//     stage (./stage.js) replaces the poster and plays the six chapters once (full motion), then idles and
//     stops. Reduced motion: no autoplay; the final frame is drawn and the chapter rail shows still frames;
//     Play is still offered. Motion "none" or Save-Data: the poster stays.
//  2. Overlay: every frame positions the DOM layer (load arrows, research panels, HUD, material names,
//     annotation leaders) from the same pure timeline, anchored to world points via the shader's camera.
//  3. Reveal-on-scroll + the CSS mini demos (run only while on screen), and the materials shelf arrows.
// Landing analytics (viewed / sections / CTAs / reason doors) come from the shared landing island, which
// the template also loads (it keys off [data-landing]).
import { motionMode } from "../scene/motion_mode.js";
import { CHAPTERS, DURATION, MATERIAL_BEATS, PHASES, chapterAt, phaseAt } from "./timeline.js";

const STILLS = [2.9, 9.4, 14.6, 19.7, 29.5, DURATION];
const clamp = (x, a = 0, b = 1) => Math.min(b, Math.max(a, x));
const sstep = (a, b, x) => {
  const t = clamp((x - a) / (b - a));
  return t * t * (3 - 2 * t);
};
const easeInOut = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
const win = (t, a, b) => clamp((t - a) / (b - a));
const bump = (t, a, b, c, d) => sstep(a, b, t) * (1 - sstep(c, d, t));

function pickTier() {
  const nav = navigator;
  if (nav.connection?.saveData) return 0;
  try {
    if (matchMedia("(prefers-reduced-data: reduce)").matches) return 0;
  } catch {
    /* unsupported query */
  }
  const mem = nav.deviceMemory || 4;
  const cores = nav.hardwareConcurrency || 4;
  if (mem <= 2) return 3;
  if (cores <= 4 || window.innerWidth < 700) return 2;
  return 1;
}

function hasWebGL2() {
  try {
    return !!document.createElement("canvas").getContext("webgl2");
  } catch {
    return false;
  }
}

// ------------------------------------------------------------------------------------------- overlay
function makeOverlay(fig) {
  const $ = (s) => fig.querySelector(s);
  const $$ = (s) => [...fig.querySelectorAll(s)];
  const loads = $$("[data-px-load]");
  const reacts = $$("[data-px-react]");
  const panels = $$("[data-px-panel]");
  const sparks = panels.map((p) => p.querySelector("path"));
  for (const s of sparks) s?.setAttribute("pathLength", "1");
  const links = $("[data-px-links]");
  const hud = $("[data-px-hud]");
  const ticks = $$("[data-px-tick]");
  const phases = $$("[data-px-phase]");
  const decay = $("[data-px-decay]");
  const decayCurve = decay?.querySelector(".px-decay__curve");
  const mats = $$("[data-px-matbeat]");
  const hands = $$("[data-px-hand]");
  const annos = $$("[data-px-annoitem]");
  const leaders = $("[data-px-leaders]");
  const frameEl = $(".px-stage__frame");
  const kicker = $("[data-px-kicker]");
  const line = $("[data-px-line]");
  const cap = $("[data-px-cap]");
  const segs = $$("[data-px-seg]");
  const fills = segs.map((s) => s.querySelector("[data-px-fill]"));
  const chapters = JSON.parse(fig.dataset.chapters || "[]");
  let lastChapter = -1;
  const NS = "http://www.w3.org/2000/svg";

  const linkPaths = [];
  if (links) {
    for (let i = 0; i < 9; i++) {
      const p = document.createElementNS(NS, "path");
      links.appendChild(p);
      linkPaths.push(p);
    }
  }
  const leaderEls = [];
  if (leaders) {
    for (let i = 0; i < 3; i++) {
      const p = document.createElementNS(NS, "path");
      const c = document.createElementNS(NS, "circle");
      c.setAttribute("r", "2.5");
      leaders.append(p, c);
      leaderEls.push({ p, c });
    }
  }

  const set = (el, x, y, o, extra = "") => {
    if (!el) return;
    el.style.opacity = o.toFixed(3);
    el.style.transform = `translate(${x.toFixed(1)}px, ${y.toFixed(1)}px) ${extra}`;
  };

  // ---- S1: the same load, three reactions
  function drawLoads(t, u, project) {
    for (let i = 0; i < loads.length; i++) {
      const I = u.inst[i];
      const pl = u.plate[i];
      const top = project([I[0], pl[0] + 0.12, 0]);
      const o = t < 6.6 ? bump(t, 0.55, 0.85, 3.6, 4.1) : 0;
      set(loads[i], top[0], top[1], o, "translate(-50%, -100%)");
      const below = project([I[0], -0.05, 0.9 * I[2]]);
      const ro = t < 6.6 ? bump(t, 1.9, 2.3, 4.3, 4.8) : 0;
      set(reacts[i], below[0], below[1] + 8, ro, "translate(-50%, 0)");
    }
  }

  // ---- S2: six perspectives orbit, then fold inward
  function drawPanels(t, c, w, h) {
    const rx = Math.min(w * 0.38, 330);
    const ry = Math.min(h * 0.34, 260);
    const pts = [];
    for (let i = 0; i < panels.length; i++) {
      const a = -Math.PI / 2 + (i * Math.PI * 2) / panels.length + (t - 6.5) * 0.04;
      const appear = sstep(6.8 + 0.28 * i, 7.4 + 0.28 * i, t);
      const fold = easeInOut(win(t, 12.7 + 0.09 * i, 13.9 + 0.09 * i));
      const fx = c[0] + Math.cos(a) * rx * (1 - fold);
      const fy = c[1] + Math.sin(a) * ry * (1 - fold) + Math.sin(t * 0.9 + i) * 4 * (1 - fold);
      const o = t > 6.4 && t < 14.6 ? appear * (1 - fold) : 0;
      set(panels[i], fx, fy, o, `translate(-50%, -50%) scale(${(0.86 + 0.14 * appear) * (1 - 0.6 * fold)})`);
      if (sparks[i]) sparks[i].style.strokeDasharray = `${clamp((t - 7.0 - 0.28 * i) / 1.2).toFixed(3)} 1`;
      pts.push([fx, fy, o]);
    }
    return pts;
  }

  function drawLinks(pts, c) {
    for (let i = 0; i < linkPaths.length; i++) {
      let d = "";
      let o = 0;
      if (i < 6 && pts[i]) {
        d = `M${pts[i][0]},${pts[i][1]} L${c[0]},${c[1]}`;
        o = pts[i][2] * 0.8;
      } else if (pts.length) {
        const a = pts[(i - 6) * 2];
        const b = pts[((i - 6) * 2 + 1) % pts.length];
        if (a && b) {
          d = `M${a[0]},${a[1]} Q${(a[0] + b[0]) / 2},${(a[1] + b[1]) / 2 - 30} ${b[0]},${b[1]}`;
          o = Math.min(a[2], b[2]) * 0.6;
        }
      }
      linkPaths[i].setAttribute("d", d || "M0,0");
      linkPaths[i].style.opacity = o.toFixed(3);
    }
  }

  // ---- S3/S4: the blueprint HUD
  function drawHud(t) {
    if (hud) hud.style.opacity = (sstep(11.9, 12.7, t) * (1 - sstep(27.3, 28.1, t))).toFixed(3);
    const ph = phaseAt(t);
    ticks.forEach((tk, i) => {
      tk.toggleAttribute("data-on", i === ph);
      tk.toggleAttribute("data-done", ph > i || t >= PHASES[PHASES.length - 1].end);
    });
    phases.forEach((el, i) => {
      const P = PHASES[i];
      const o = bump(t, P.start + 0.05, P.start + 0.4, P.end - 0.25, P.end + 0.05);
      el.style.opacity = o.toFixed(3);
      el.style.transform = `translateX(${((1 - o) * 12).toFixed(1)}px)`;
    });
    if (decay) {
      decay.style.opacity = bump(t, 23.5, 23.9, 26.0, 26.5).toFixed(3);
      if (decayCurve) decayCurve.style.strokeDashoffset = (1 - win(t, 23.6, 25.7)).toFixed(3);
    }
  }

  // ---- S5: material names
  function drawMaterials(t) {
    mats.forEach((el, i) => {
      const B = MATERIAL_BEATS[i];
      const o = bump(t, B.start, B.start + 0.35, B.end - 0.3, B.end);
      el.style.opacity = o.toFixed(3);
      el.style.transform = `translateY(${((1 - o) * 10).toFixed(1)}px)`;
    });
  }

  // ---- S6: annotations, margin notes, leader lines
  function drawNotes(t, u, project) {
    annos.forEach((el, i) => el.toggleAttribute("data-on", t >= 35.7 + i * 0.3));
    hands.forEach((el) => (el.style.opacity = sstep(36.2, 37.4, t).toFixed(3)));
    if (leaders && frameEl) {
      const fr = frameEl.getBoundingClientRect();
      const s = u.inst[0][2];
      const anchors = [
        [0.62 * s, 1.55 * s, 0.55 * s],
        [0.98 * s, 0.9 * s, 0.25 * s],
        [0.18 * s, 0.56 * s, 1.15 * s],
      ];
      leaderEls.forEach(({ p, c: dot }, i) => {
        const card = annos[i];
        const visible = card && card.hasAttribute("data-on") && getComputedStyle(card.parentElement).position === "absolute";
        const r = card?.getBoundingClientRect();
        const desktop = r && r.left > fr.left + fr.width * 0.5;
        if (!visible || !desktop) {
          p.style.opacity = "0";
          dot.style.opacity = "0";
          return;
        }
        const a = project(anchors[i]);
        const ex = r.left - fr.left - 6;
        const ey = r.top - fr.top + r.height / 2;
        const k = sstep(35.9 + i * 0.3, 36.8 + i * 0.3, t);
        const mx = a[0] + (ex - a[0]) * 0.55;
        p.setAttribute("d", `M${a[0]},${a[1]} L${mx},${ey} L${ex},${ey}`);
        p.style.opacity = (k * 0.9).toFixed(3);
        dot.setAttribute("cx", a[0].toFixed(1));
        dot.setAttribute("cy", a[1].toFixed(1));
        dot.style.opacity = k.toFixed(3);
      });
    }
  }

  // ---- caption + chapter rail
  function drawCaption(t) {
    const ci = chapterAt(t);
    if (ci !== lastChapter && chapters[ci]) {
      lastChapter = ci;
      kicker.textContent = chapters[ci].kicker;
      line.textContent = chapters[ci].line;
      cap.removeAttribute("data-swap");
      void cap.offsetWidth;
      cap.setAttribute("data-swap", "");
      segs.forEach((s, i) => (i === ci ? s.setAttribute("aria-current", "step") : s.removeAttribute("aria-current")));
    }
    CHAPTERS.forEach((ch, i) => {
      if (fills[i]) fills[i].style.transform = `scaleX(${clamp((t - ch.start) / (ch.end - ch.start)).toFixed(3)})`;
    });
  }

  return function update(t, u, view) {
    const { w, h, project } = view;
    links?.setAttribute("viewBox", `0 0 ${w} ${h}`);
    leaders?.setAttribute("viewBox", `0 0 ${w} ${h}`);
    drawLoads(t, u, project);
    const c = project([0, 0.95, 0]);
    drawLinks(drawPanels(t, c, w, h), c);
    drawHud(t);
    drawMaterials(t);
    drawNotes(t, u, project);
    drawCaption(t);
  };
}

// ------------------------------------------------------------------------------------------- stage boot
async function bootStage(fig) {
  const mode = motionMode();
  const tier = pickTier();
  if (mode === "none" || tier === 0) return;
  const frameEl = fig.querySelector(".px-stage__frame");
  const canvas = fig.querySelector("[data-px-canvas]");
  const ctrl = fig.querySelector("[data-px-controls]");
  const toggle = fig.querySelector("[data-px-toggle]");
  const update = makeOverlay(fig);
  const reduced = mode === "reduced";
  const sync = (st) => {
    const paused = !st.playing;
    toggle.toggleAttribute("data-paused", paused);
    toggle.setAttribute("aria-label", paused ? toggle.dataset.labelPlay : toggle.dataset.labelPause);
  };
  const live = () => fig.hasAttribute("data-live") || fig.setAttribute("data-live", "");
  const onFrame = (t, u, view) => {
    if (fig.dataset.player === "stage") live();
    update(t, u, view);
  };
  let stage = null;
  const startFilm = async (at = null) => {
    let film = {};
    try {
      film = JSON.parse(fig.dataset.film || "{}");
    } catch {
      film = {};
    }
    const big = frameEl.clientWidth * Math.min(window.devicePixelRatio || 1, 2) > 700;
    const set = (big ? film.lg : film.sm) || film.lg || film.sm;
    if (!set) return null;
    const { createFilm } = await import("./video.js");
    const sources = [];
    if (set.webm) sources.push({ src: set.webm, type: 'video/webm; codecs="vp9"' });
    if (set.mp4) sources.push({ src: set.mp4, type: "video/mp4" });
    const f = createFilm(frameEl, {
      sources,
      onFrame,
      onState: sync,
      onFirst: () => {
        fig.dataset.player = "film";
        live();
      },
    });
    if (at !== null) f.seek(at, { play: true });
    return f;
  };
  // Live raymarch when the GPU passes the cost probe (./stage.js); otherwise, or if it later cannot keep up,
  // the pre-rendered film of the same timeline.
  const forceFilm = new URLSearchParams(location.search).has("px_film"); // QA
  if (tier <= 2 && hasWebGL2() && !forceFilm) {
    const { createStage } = await import("./stage.js");
    fig.dataset.player = "stage";
    stage = await createStage(canvas, {
      tier,
      onFrame,
      onState: sync,
      onTooSlow: async (at) => {
        const f = await startFilm(at);
        if (!f) return;
        const old = stage;
        stage = f;
        window.__pressure = f;
        old.dispose();
      },
    });
  }
  if (!stage) {
    stage = await startFilm();
    if (!stage) return;
  }
  window.__pressure = stage;
  ctrl.hidden = false;
  bindControls(fig, toggle, () => stage, reduced);
  startPlayback(fig, stage, reduced, sync);
}

/** Chapter rail, play/pause, replay and skip; `current()` is the live player (the stage may hand over to the film). */
function bindControls(fig, toggle, current, reduced) {
  fig.querySelectorAll("[data-px-seg]").forEach((b, i) =>
    b.addEventListener("click", () => (reduced && !current().state().playing ? current().seek(STILLS[i], { play: false }) : current().seek(CHAPTERS[i].start, { play: true }))),
  );
  toggle.addEventListener("click", () => current().toggle());
  fig.querySelector("[data-px-replay]")?.addEventListener("click", () => current().replay());
  fig.querySelector("[data-px-skip]")?.addEventListener("click", () => current().seek(DURATION, { play: !reduced && fig.dataset.player === "stage" }));
}

/** First frame: a QA still, the final frame (reduced motion), or play from the top. */
function startPlayback(fig, stage, reduced, sync) {
  const q = new URLSearchParams(location.search).get("px_t"); // QA: ?px_t=12.5 renders one still
  if (q !== null) stage.seek(parseFloat(q) || 0, { play: false });
  else if (reduced) {
    if (fig.dataset.player === "stage") stage.seek(DURATION, { play: false });
    else sync(stage.state()); // the film waits on the poster until Play
  } else stage.seek(0, { play: true });
}

function whenNear(el, fn) {
  if (!("IntersectionObserver" in window)) return fn();
  const io = new IntersectionObserver(
    (entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        io.disconnect();
        fn();
      }
    },
    { rootMargin: "200px" },
  );
  io.observe(el);
}

function idle(fn) {
  const go = () => ("requestIdleCallback" in window ? requestIdleCallback(fn, { timeout: 1500 }) : setTimeout(fn, 300));
  if (document.readyState === "complete") go();
  else addEventListener("load", go, { once: true });
}

// ------------------------------------------------------------------------------------------- page bits
function bindReveal(root) {
  if (!("IntersectionObserver" in window)) return;
  const once = new IntersectionObserver(
    (entries) => {
      for (const e of entries)
        if (e.isIntersecting) {
          e.target.classList.add("is-in");
          once.unobserve(e.target);
        }
    },
    { threshold: 0.2 },
  );
  for (const el of root.querySelectorAll("[data-px-reveal]")) once.observe(el);
  const live = new IntersectionObserver((entries) => {
    for (const e of entries) e.target.classList.toggle("is-live", e.isIntersecting);
  });
  for (const el of root.querySelectorAll(".px-mech")) live.observe(el);
}

function bindShelf(root) {
  const list = root.querySelector("[data-px-shelf]");
  if (!list) return;
  const btns = [...root.querySelectorAll("[data-px-shelf-nav]")];
  const sync = () => {
    const max = list.scrollWidth - list.clientWidth - 2;
    btns.forEach((b) => (b.hidden = b.dataset.pxShelfNav === "-1" ? list.scrollLeft <= 2 : list.scrollLeft >= max));
  };
  btns.forEach((b) => b.addEventListener("click", () => list.scrollBy({ left: Number(b.dataset.pxShelfNav) * list.clientWidth * 0.8, behavior: "smooth" })));
  list.addEventListener("scroll", sync, { passive: true });
  addEventListener("resize", sync, { passive: true });
  sync();
}

function init() {
  const root = document.querySelector(".px");
  if (!root || root.dataset.js) return;
  root.dataset.js = "1";
  bindReveal(root);
  bindShelf(root);
  const fig = root.querySelector("[data-px-stage]");
  if (fig) idle(() => whenNear(fig, () => bootStage(fig).catch((e) => console.warn("[pressure]", e))));
}

init();
document.addEventListener("htmx:afterSettle", init);
